export const ACCENT = "#2563EB";
export const C = { cyan: "#2DD4BF", purple: "#3B82F6", blue: "#2563EB", red: "#F43F5E", yellow: "#F59E0B", green: "#10B981", grey: "#64748b" };
export const SITE_COLORS = { Qatar: C.cyan, India: C.yellow, Unknown: C.grey };

export const mean = (a) => (a.length ? a.reduce((x, y) => x + y, 0) / a.length : null);
export const median = (a) => {
  if (!a.length) return null;
  const s = [...a].sort((x, y) => x - y);
  return s.length % 2 ? s[(s.length - 1) / 2] : (s[s.length / 2 - 1] + s[s.length / 2]) / 2;
};
export const fmtN = (n, d = 1) => (n == null || isNaN(n) ? "—" : Number(n).toLocaleString(undefined, { maximumFractionDigits: d }));
export const fmtPct = (n) => (n == null || isNaN(n) ? "—" : `${Number(n).toFixed(1)}%`);
export const fmtInt = (n) => (n == null || isNaN(n) ? "—" : Math.round(n).toLocaleString());

export const siteOf = (data, c) => data.agents[c]?.site || "Unknown";

export function filterData(data, { from, to, site }) {
  const ok = (c) => site === "All" || siteOf(data, c) === site;
  const inR = (d) => d && d >= from && d <= to;
  const f = (rows) => rows.filter((r) => inR(r.d) && ok(r.c));
  return { voc: f(data.voc), answered: f(data.answered), held: f(data.held), hold: f(data.hold), talk: f(data.talk), aht: f(data.aht), pmt: f(data.pmt), qsl: f(data.qsl) };
}

export function kpisOf(sl) {
  const n = sl.voc.reduce((a, r) => a + r.n, 0);
  const t2 = sl.voc.reduce((a, r) => a + r.t2b, 0);
  const ans = sl.answered.reduce((a, r) => a + r.v, 0);
  const heldN = sl.held.reduce((a, r) => a + r.v, 0);
  const ps = sl.pmt.map((p) => p.score).filter((v) => v != null);
  const qs = sl.qsl.map((q) => q.score).filter((v) => v != null);
  return {
    voc: n ? (100 * t2) / n : null, surveys: n,
    answered: ans, heldPct: ans ? (100 * heldN) / ans : null,
    hold: mean(sl.hold.map((r) => r.v)), talk: mean(sl.talk.map((r) => r.v)), aht: mean(sl.aht.map((r) => r.v)),
    pmt: mean(ps), evals: sl.pmt.length, crit: sl.pmt.filter((p) => p.rating === "Critical").length,
    qsl: qs.length ? mean(qs) : null, audits: sl.qsl.length,
  };
}

export function dailySeries(rows, fn) {
  const by = {};
  rows.forEach((r) => { (by[r.d] = by[r.d] || []).push(r); });
  return Object.keys(by).sort().map((d) => ({ d, v: fn(by[d]) })).filter((p) => p.v != null);
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
export function periodOf(d, mode) {
  const [y, m, day] = d.split("-").map(Number);
  if (mode === "m") return { key: d.slice(0, 7), label: `${MONTHS[m - 1]} ${String(y).slice(2)}` };
  const w = day <= 7 ? 1 : day <= 14 ? 2 : day <= 21 ? 3 : day <= 28 ? 4 : 5;
  return { key: `${d.slice(0, 7)}-W${w}`, label: `${MONTHS[m - 1]} W${w}` };
}

export function trendSeries(data, { mode, ciscos, site }) {
  const okC = ciscos ? (c) => ciscos.has(c) : site && site !== "All" ? (c) => siteOf(data, c) === site : () => true;
  const buckets = {};
  const push = (feed, r) => {
    if (!r.d || !okC(r.c)) return;
    const p = periodOf(r.d, mode);
    const b = (buckets[p.key] = buckets[p.key] || { key: p.key, label: p.label, voc: [], answered: [], held: [], hold: [], talk: [], aht: [], pmt: [], qsl: [] });
    b[feed].push(r);
  };
  ["voc", "answered", "held", "hold", "talk", "aht", "pmt", "qsl"].forEach((f) => data[f].forEach((r) => push(f, r)));
  const out = Object.values(buckets).sort((a, b) => (a.key < b.key ? -1 : 1)).map((b) => ({ key: b.key, label: b.label, ...kpisOf(b) }));
  if (mode === "m") {
    const md = {};
    data.monthly.forEach((r) => {
      if (!okC(r.c)) return;
      const b = (md[r.m] = md[r.m] || { voc: [], q: [], adh: [] });
      if (r.voc != null) b.voc.push(r.voc);
      if (r.q != null) b.q.push(r.q);
      if (r.adh != null) b.adh.push(r.adh);
    });
    out.forEach((o) => {
      const b = md[o.key];
      if (b) { o.mdVoc = mean(b.voc); o.mdQ = mean(b.q); o.mdAdh = mean(b.adh); }
    });
    Object.keys(md).forEach((m) => {
      if (!out.some((o) => o.key === m)) {
        const b = md[m];
        out.push({ key: m, label: `${MONTHS[Number(m.slice(5, 7)) - 1]} ${m.slice(2, 4)}`, mdVoc: mean(b.voc), mdQ: mean(b.q), mdAdh: mean(b.adh) });
      }
    });
    out.sort((a, b) => (a.key < b.key ? -1 : 1));
  }
  return out;
}

export function computeAgentRows(data, sl) {
  const by = {};
  const g = (c) => (by[c] = by[c] || { voc: [], answered: [], held: [], hold: [], talk: [], aht: [], pmt: [], qsl: [] });
  Object.entries(sl).forEach(([feed, rows]) => rows.forEach((r) => g(r.c)[feed].push(r)));
  return Object.keys(data.agents).map((c) => {
    const a = data.agents[c];
    const k = kpisOf(by[c] || { voc: [], answered: [], held: [], hold: [], talk: [], aht: [], pmt: [], qsl: [] });
    const cr = data.critical[c];
    const ro = data.roster[c];
    const md = data.monthly.filter((m) => m.c === c).sort((x, y) => (x.m < y.m ? -1 : 1)).slice(-1)[0];
    const adh = ro?.adh ?? md?.adh ?? null;
    const parts = [k.voc, k.pmt, k.qsl, adh].filter((v) => v != null);
    return {
      c, name: a.name || `Agent ${c}`, grp: a.tl || a.team || "—", site: a.site,
      ...k, fatals: cr?.fatal ?? null, adh, composite: parts.length ? mean(parts) : null,
    };
  });
}

export function computeGroupRows(data, sl) {
  const groups = {};
  Object.entries(data.agents).forEach(([c, a]) => {
    const grp = a.tl || a.team;
    if (grp && grp !== "-") (groups[grp] = groups[grp] || new Set()).add(c);
  });
  return Object.entries(groups).map(([grp, ciscos]) => {
    const team = {};
    ["voc", "answered", "held", "hold", "talk", "aht", "pmt", "qsl"].forEach((f) => { team[f] = sl[f].filter((r) => ciscos.has(r.c)); });
    const k = kpisOf(team);
    return { grp, agents: ciscos.size, ciscos, ...k };
  });
}

export function sortRows(rows, key, dir) {
  return [...rows].sort((a, b) => {
    const av = a[key], bv = b[key];
    if (av == null && bv == null) return 0;
    if (av == null) return 1;
    if (bv == null) return -1;
    if (typeof av === "string") return dir * av.localeCompare(bv);
    return dir * (av - bv);
  });
}

export function presetsFor(data) {
  const dates = [data.coverage.voc, data.coverage.calls, data.coverage.qsl].filter(Boolean);
  const latest = dates.length ? dates.sort().slice(-1)[0] : new Date().toISOString().slice(0, 10);
  const m = latest.slice(0, 7);
  return {
    latest, month: m,
    list: [
      { id: "w1", label: "W1", from: `${m}-01`, to: `${m}-07` },
      { id: "w2", label: "W2", from: `${m}-08`, to: `${m}-14` },
      { id: "w3", label: "W3", from: `${m}-15`, to: `${m}-21` },
      { id: "w4", label: "W4", from: `${m}-22`, to: `${m}-28` },
      { id: "mtd", label: "MTD", from: `${m}-01`, to: latest },
      { id: "all", label: "All", from: "2000-01-01", to: "2099-12-31" },
    ],
  };
}

export const covLabel = (d) => {
  if (!d) return "no data";
  const parts = d.split("-").map(Number);
  return parts.length === 3 ? `${MONTHS[parts[1] - 1]} ${parts[2]}` : `${MONTHS[parts[1] - 1]} ${parts[0]}`;
};

export const IV_METRICS = (t) => [
  { k: "voc", label: "VOC Top-2-Box", pct: true, target: t.voc },
  { k: "surveys", label: "Surveys", int: true },
  { k: "answered", label: "Answered Calls", int: true },
  { k: "heldPct", label: "Held Calls %", pct: true, lower: true },
  { k: "hold", label: "Avg Hold (sec)", lower: true, d: 0 },
  { k: "talk", label: "Avg Talk (sec)", lower: true, d: 0 },
  { k: "aht", label: "AHT (sec)", lower: true, d: 0, target: t.aht },
  { k: "pmt", label: "PMT Score", pct: true, target: t.pmt },
  { k: "crit", label: "PMT Criticals", int: true, lower: true },
  { k: "qsl", label: "QSL Score", pct: true, target: t.qsl },
];
